import type { Ref } from "react";
import { useRef } from "react";
import {
  getMatcherTypesDisplayValue,
  isMatcherTypes,
  MatcherTypes,
} from "../../types/rules/MatcherTypes";
import { setRef } from "../../utils/SetRef";
import { useDropdown } from "../hooks/useDropdown";
import Icon from "../Icon";
import { Label, LabelProps } from "./Label";
import "../../styles/inputGroup/dropdownLabel.css";

export { MatcherTypeLabel, Props as MatcherTypeLabelProps };
type Props = {
  /**
   * Current MatcherMatcher discriminator.
   */
  discriminator: MatcherTypes;
  onMatcherChange(newType: MatcherTypes): void;
  filter?: MatcherTypes[];
} & LabelProps;
const types: MatcherTypes[] = ["M_FUZZY", "M_REGEX", "M_LOCATION"];
/**
 * An <InputGroup/> label element that allows for the MatcherType to be changed
 */
const MatcherTypeLabel = ({
  ref,
  ...props
}: Props & {
  ref: Ref<HTMLLabelElement>;
}) => {
  const containerRef = useRef<HTMLLabelElement | null>(null);
  const allowedTypes = props.filter ?? types;

  const { isOpen, renderTooltip, setOpen, triggerBounds, triggerProps } =
    useDropdown({
      containerRef,
      options: allowedTypes.reduce((prev, curr) => {
        //@ts-expect-error
        prev[curr] = getMatcherTypesDisplayValue(curr as MatcherTypes);
        return prev;
      }, {}),
      selected: props.discriminator,
      onSelect: (val) => {
        if (isMatcherTypes(val)) {
          props.onMatcherChange(val);
        }
      },
    });

  return (
    <Label
      {...props}
      className={"dropdownLabel"}
      ref={(r) => {
        containerRef.current = r;
        setRef(ref, r);
      }}
    >
      {props.children}
      <Icon
        {...triggerProps}
        onClick={() => setOpen(!isOpen)}
        altText={"Change Type"}
        icon={"chevron-down"}
      />
      {isOpen && renderTooltip()}
    </Label>
  );
};
